import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import MerchantEditModal from './MerchantEditModal';
import TableToggle from './TableToggle';
import {
  ENABLED_FILTER_OPTIONS,
  filterMerchants,
  maskSecret,
} from './merchantConstants';
import { buildCoinPayTypePageParams } from './coinPayTypeConstants';
import { buildCurrencyOptionsFromCoinPayTypes } from './paymentAssetConstants';

const EMPTY_FILTERS = {
  name: '',
  merchantCode: '',
  currencyType: '',
  isEnabled: '',
};

const TABLE_COLUMNS = [
  'ID',
  '名稱',
  '商戶 Code',
  '商戶 ID',
  '貨幣類型',
  '排序',
  '狀態',
  '操作',
];

export default function OnlineMerchantPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const filteredRecords = useMemo(
    () => filterMerchants(records, filters),
    [records, filters]
  );

  async function loadCurrencyOptions() {
    try {
      const data = await adminApi.coinPayTypePage(buildCoinPayTypePageParams(1, {}));
      setCurrencyOptions(buildCurrencyOptionsFromCoinPayTypes(data?.records || []));
    } catch {
      setCurrencyOptions(buildCurrencyOptionsFromCoinPayTypes([]));
    }
  }

  async function loadRecords() {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listMerchants();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    loadCurrencyOptions();
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
  }

  function handleReset() {
    setFilters({ ...EMPTY_FILTERS });
    setMessage('');
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateMerchant(payload);
        setMessage('已更新');
      } else {
        await adminApi.createMerchant(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords();
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggle(row, nextValue) {
    if (!row?.id) {
      return;
    }
    const toggleKey = `${row.id}-isEnabled`;
    setTogglingKey(toggleKey);
    setError('');
    try {
      await adminApi.updateMerchant({ id: row.id, isEnabled: nextValue });
      setRecords((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, isEnabled: nextValue } : item))
      );
    } catch (err) {
      setError(err.message || '更新狀態失敗');
    } finally {
      setTogglingKey(null);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>線上商戶</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增商戶
        </button>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="omName">名稱 / 商戶 ID</label>
          <input
            id="omName"
            type="text"
            value={filters.name}
            onChange={(e) => updateFilter('name', e.target.value)}
            placeholder="模糊搜尋"
          />
        </div>
        <div className="form-field">
          <label htmlFor="omMerchantCode">商戶 Code</label>
          <input
            id="omMerchantCode"
            type="text"
            value={filters.merchantCode}
            onChange={(e) => updateFilter('merchantCode', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="omCurrencyType">貨幣類型</label>
          <input
            id="omCurrencyType"
            type="text"
            value={filters.currencyType}
            onChange={(e) => updateFilter('currencyType', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="omIsEnabled">狀態</label>
          <select
            id="omIsEnabled"
            value={filters.isEnabled}
            onChange={(e) => updateFilter('isEnabled', e.target.value)}
          >
            {ENABLED_FILTER_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && (
        <p className="page-desc">
          共 {filteredRecords.length} 筆
          {filteredRecords.length !== records.length ? `（全部 ${records.length} 筆）` : ''}
        </p>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && filteredRecords.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              filteredRecords.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td className="mono-cell">{row.merchantCode}</td>
                  <td className="mono-cell" title={row.merchantId}>
                    {maskSecret(row.merchantId)}
                  </td>
                  <td>{row.currencyType}</td>
                  <td>{row.sort ?? 0}</td>
                  <td className="table-toggle-cell">
                    <TableToggle
                      checked={row.isEnabled}
                      disabled={togglingKey === `${row.id}-isEnabled`}
                      ariaLabel={`${row.name} 狀態`}
                      onChange={(v) => handleToggle(row, v)}
                    />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        編輯
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <MerchantEditModal
        open={editOpen}
        row={editRow}
        currencyOptions={currencyOptions}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />
    </div>
  );
}
